import React from 'react'
import {
  CountriesAlpha2,
  Currency,
  useLoadAllItemsQuery,
  useMutation,
  useQuery
} from '@sequencehq/api'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import {
  dashboard20240730Client,
  type DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import {
  Checkbox,
  DatePickerField,
  RadioButtonGroupField,
  InputSelectControlField,
  TextInputField,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { toCurrencySymbol, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import invariant from 'tiny-invariant'
import { format } from '@sequencehq/utils/dates'
import { UsageMetricSelect } from 'UsageMetrics/index'
import { CurrencyInputSelectControl } from 'components/CurrencyInputSelectControl/CurrencyInputSelectControl'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'

type PostCreditGrantBody =
  DashboardApi20240730.PostCreditGrant.PostCreditGrantBody

type FormValues = {
  name: string
  amount: string
  usageMetricId: string
  costOfCredit: string
  creditUnitType: 'CURRENCY' | 'METRIC'
  currency: Currency
  createInvoice: boolean
  expiryDate?: string
  taxRate?: string
}

type CreateCreditGrantInput = {
  name: string
  amount: number
  costOfCredit: number
  creditUnitType: 'CURRENCY' | 'METRIC'
  currency: Currency
  createInvoice: boolean
  expiryDate?: string
  metricId?: string
}

const getCashCreditGrantBody = (input: CreateCreditGrantInput) => {
  return {
    name: input.name,
    amount: input.amount,
    costOfCredit: input.amount,
    currency: input.currency,
    expiryDate: input.expiryDate
      ? format(new Date(input.expiryDate), 'yyyy-MM-dd')
      : undefined,
    createInvoice: input.createInvoice
  }
}

const getUsageCreditGrantBody = (input: CreateCreditGrantInput) => {
  return {
    name: input.name,
    amount: input.amount,
    costOfCredit: input.costOfCredit,
    currency: input.currency,
    metricId: input.metricId,
    expiryDate: input.expiryDate
      ? format(new Date(input.expiryDate), 'yyyy-MM-dd')
      : undefined,
    createInvoice: input.createInvoice
  }
}

const useCreateCreditGrant = ({ customerId }: { customerId: string }) => {
  const sequenceAccountSettings = useQuery(
    dashboardv20240509Client.getSequenceAccountSettings
  )
  const sequenceAccountId = sequenceAccountSettings?.data?.sequenceAccountId

  const { mutateAsync } = useMutation(dashboard20240730Client.postCreditGrant)

  const createCreditGrant = async (input: CreateCreditGrantInput) => {
    invariant(sequenceAccountId, 'Sequence account not found')

    const creditUnitTypeSpecificBody =
      input.creditUnitType === 'CURRENCY'
        ? getCashCreditGrantBody(input)
        : getUsageCreditGrantBody(input)

    const createCreditGrantBody: PostCreditGrantBody = {
      customerId,
      sequenceAccountId,
      creditUnitType: input.creditUnitType,
      ...creditUnitTypeSpecificBody
    }

    return await mutateAsync({ body: createCreditGrantBody })
  }

  return {
    createCreditGrant
  }
}

interface Props {
  customerId: string
  // Undefined for an address-less customer — no country tax rates then match.
  customerCountry: CountriesAlpha2 | undefined
  trigger: React.ReactElement
}

export const CreateCreditGrantDrawer = ({
  customerId,
  customerCountry,
  trigger
}: Props) => {
  const { displayPromiseNotification } = useNotifications()
  const taxRatesQuery = useLoadAllItemsQuery(
    dashboardv99990101Client.getTaxRates
  )
  const taxRates =
    taxRatesQuery?.data?.pages
      ?.flatMap(page => page.items)
      .filter(taxRate => taxRate.country === customerCountry) ?? []

  const taxRateOptions = taxRates.map(taxRate => ({
    label: taxRate.name,
    value: taxRate.id
  }))

  const { createCreditGrant } = useCreateCreditGrant({
    customerId
  })

  const form = useForm<FormValues>({
    value: {
      name: '',
      amount: '',
      usageMetricId: '',
      costOfCredit: '',
      creditUnitType: 'METRIC',
      currency: 'GBP',
      createInvoice: true,
      expiryDate: undefined,
      taxRate: ''
    },
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'amount',
        validation: [required]
      },
      {
        property: 'usageMetricId',
        validation: [required]
      },
      {
        property: 'costOfCredit',
        validation: [required]
      },
      {
        property: 'creditUnitType',
        validation: [required]
      },
      {
        property: 'currency',
        validation: [required]
      },
      {
        property: 'createInvoice'
      },
      {
        property: 'taxRate',
        validation: []
      },
      {
        property: 'expiryDate',
        validation: []
      }
    ]
  })

  const handleSubmit = async () => {
    const input: CreateCreditGrantInput = {
      name: form.fields.name.value,
      amount: Number.parseFloat(form.fields.amount.value),
      costOfCredit: Number.parseFloat(form.fields.costOfCredit.value),
      creditUnitType: form.fields.creditUnitType.value,
      currency: form.fields.currency.value,
      createInvoice: form.fields.createInvoice.value,
      expiryDate: form.fields.expiryDate.value,
      metricId: form.fields.usageMetricId.value
    }

    try {
      await displayPromiseNotification(createCreditGrant(input), {
        loading: 'Creating credit grant...',
        success: 'Credit grant created',
        error: 'Failed to create credit grant'
      })
      return true
    } catch {
      form.mutators.resetFormData()
      return false
    }
  }

  const getPricePerUnit = (costOfCredit?: string, amount?: string) => {
    if (!costOfCredit || !amount) {
      return ''
    }

    const numericCost = Number.parseFloat(costOfCredit as string)
    const numericAmount = Number.parseFloat(amount as string)

    return (numericCost / numericAmount).toString()
  }

  const pricePerUnit = getPricePerUnit(
    form.queries.formData.costOfCredit,
    form.queries.formData.amount
  )

  return (
    <SimpleModal
      trigger={trigger}
      variant="drawer"
      title="Add new credit grant"
      cancelButtonText="Cancel"
      submitButtonText="Save credit grant"
      submittingButtonText="Saving credit grant..."
      onSubmit={handleSubmit}
      onClose={form.mutators.resetFormData}
    >
      <Flex
        flexDirection="column"
        gap="16px"
        data-testid="createCreditGrant.form"
      >
        <TextInputField
          tooltip={{
            title: 'Name of credit grant',
            content:
              'The name, balance and expiry date of the credit grant will be displayed on each invoice when credits are used.'
          }}
          label="Name"
          placeholder="Enter the name for the credit grant"
          value={form.fields.name.value}
          onChange={e => form.fields.name.onChange(e.target.value)}
          validationErrors={form.fields.name.validationErrors}
        />
        <RadioButtonGroupField
          data-testid="creditNoteLineItemDrawer.rate"
          label="Credit type"
          fullWidth
          value={form.fields.creditUnitType.value}
          options={[
            { label: 'Usage', value: 'METRIC' },
            { label: 'Cash', value: 'CURRENCY' }
          ]}
          validationErrors={[]}
          onChange={value =>
            form.fields.creditUnitType.onChange(
              value as DashboardApi20240730.CreditGrants.CreditGrantUnitType
            )
          }
        />
        {form.queries.formData.creditUnitType === 'METRIC' && (
          <>
            <UsageMetricSelect
              tooltip={{
                title: 'Usage metric',
                content:
                  'Select the usage metric that refers to the usage event you want to credit.'
              }}
              onChange={value => form.fields.usageMetricId.onChange(value)}
              value={form.fields.usageMetricId.value}
            />

            <TextInputField
              type="number"
              label="Number of units"
              placeholder="Enter number of units"
              value={form.fields.amount.value}
              onChange={e => form.fields.amount.onChange(e.target.value)}
              validationErrors={form.fields.amount.validationErrors}
            />
          </>
        )}

        <CurrencyInputSelectControl
          label="Currency"
          placeholder="Select currency"
          onChange={value => form.fields.currency.onChange(value)}
          value={form.fields.currency.value}
        />
        {form.queries.formData.creditUnitType === 'METRIC' && (
          <>
            <Flex justifyContent="space-between" gap={4}>
              <TextInputField
                type="number"
                label="Total price"
                placeholder="Enter value"
                value={form.fields.costOfCredit.value}
                onChange={e =>
                  form.fields.costOfCredit.onChange(e.target.value)
                }
                prefix={
                  form.queries.formData.currency
                    ? toCurrencySymbol(
                        form.queries.formData.currency as Currency
                      )
                    : '-'
                }
              />
              <TextInputField
                label="Price per unit"
                isDisabled={true}
                value={pricePerUnit}
                prefix={`${
                  form.queries.formData.currency
                    ? toCurrencySymbol(
                        form.queries.formData.currency as Currency
                      )
                    : '-'
                } / unit`}
              />
            </Flex>
          </>
        )}
        {form.queries.formData.creditUnitType === 'CURRENCY' && (
          <TextInputField
            type="number"
            label="Amount of credit grant"
            placeholder="Enter value"
            value={form.fields.amount.value}
            onChange={e => form.fields.amount.onChange(e.target.value)}
            prefix={
              form.queries.formData.currency
                ? toCurrencySymbol(form.queries.formData.currency)
                : '-'
            }
          />
        )}
        {form.queries.formData.creditUnitType === 'METRIC' && (
          <InputSelectControlField
            label="Tax rate"
            placeholder="Choose tax rate"
            options={taxRateOptions}
            onChange={value => form.fields.taxRate.onChange(value)}
            initialValue={form.fields.taxRate.value}
          />
        )}
        <DatePickerField
          width="100%"
          label="Expiry date"
          placeholder="Select date"
          value={
            form.fields.expiryDate.value
              ? new Date(form.fields.expiryDate.value)
              : undefined
          }
          onChange={value =>
            form.fields.expiryDate.onChange(
              value ? format(value, 'yyyy-MM-dd') : ''
            )
          }
        />
        <Checkbox
          data-testid="creditGrantForm.createInvoice"
          label="Create a draft invoice to charge this customer for the credits. Disable this to grant credits without an invoice."
          isChecked={form.fields.createInvoice.value}
          onChange={e => form.fields.createInvoice.onChange(e.target.checked)}
        />
      </Flex>
    </SimpleModal>
  )
}
